import { useEffect, useRef, useState } from "react";
import { Button } from "../ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { toast } from "sonner";
import { FileText, Target, Users, DollarSign, Download, Sparkles, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import CreditCostTag from "../CreditCostTag";

import { ProblemTab } from "./define/ProblemTab";
import { SmartGoalTab } from "./define/SmartGoalTab";
import { SipocTab } from "./define/SipocTab";
import { StakeholdersTab } from "./define/StakeholdersTab";
import { FinancialTab } from "./define/FinancialTab";
import {
  normalizeFinancialImpactForm,
  normalizeFinancialImpactResponse,
  prepareFinancialImpactForSave,
  validateFinancialImpactForm,
} from "./define/financialImpact";
import { getDesiredStateWarning, validateProblemStatement } from "./define/problemStatement";
import { getSmartMeasurableWarning } from "./define/smartGoal";
import { normalizeSipocDraftItems, scoreSipocQuality, SIPOC_QUALITY_VALID_THRESHOLD, validateSipoc } from "./define/sipoc";
import {
  createBlankStakeholder,
  normalizeStakeholderDraft,
  STAKEHOLDER_TEMPLATE,
} from "./define/stakeholders";
import { hasOperationalDefinitionContent, normalizeOperationalDefinitionState } from "./define/operationalDefinition";
import { getProjectSipocSuggestRoute } from "./define/apiRoutes";
import { handleAiResponse, unwrapAi } from "../../utils/aiResponse";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEFINE_GATE_LABELS = {
  problem_statement: "Problem Statement",
  business_impact: "Business Impact",
  smart_goal: "SMART Goal",
  sipoc: "SIPOC",
  stakeholders: "Stakeholders",
  financials: "Financials",
};

const SIPOC_SECTION_KEYS = ["suppliers", "inputs", "process_steps", "outputs", "customers"];

function createSipocSectionSelectionState(value = true) {
  return Object.fromEntries(SIPOC_SECTION_KEYS.map((key) => [key, value]));
}

function createSipocSectionModeState(currentSipoc = {}) {
  return Object.fromEntries(
    SIPOC_SECTION_KEYS.map((key) => [key, "replace"])
  );
}

function mergeSipocSectionItems(currentItems = [], draftItems = []) {
  const merged = [];
  const seen = new Set();
  [...currentItems, ...draftItems].forEach((item) => {
    const normalized = String(item || "").trim();
    if (!normalized) return;
    const signature = normalized.toLowerCase();
    if (seen.has(signature)) return;
    seen.add(signature);
    merged.push(normalized);
  });
  return merged.length ? merged : [""];
}

function resolveSipocSectionApplication(currentItems = [], draftItems = [], mode = "replace") {
  if (mode === "keep") {
    return currentItems?.length ? currentItems : [""];
  }
  if (mode === "merge") {
    return mergeSipocSectionItems(currentItems, draftItems);
  }
  return draftItems?.length ? draftItems : [""];
}

function normalizeDefineList(items) {
  return Array.isArray(items) && items.length ? items : [""];
}

function normalizeProblemStatementState(value) {
  const payload = value && typeof value === "object" ? value : {};
  return {
    problem_text: String(payload.problem_text || payload.refined_problem_statement || payload.refined || "").trim(),
    current_state: String(payload.current_state || payload.current_condition || payload.estimated_baseline || "").trim(),
    desired_state: String(payload.desired_state || payload.project_objective || payload.suggested_goal || "").trim(),
    impact: String(payload.impact || payload.business_impact || "").trim(),
    scope: String(payload.scope || payload.recommended_scope || "").trim(),
  };
}

function getFollowUpQuestionKey(item) {
  return `${String(item?.field || "").trim()}::${String(item?.category || "").trim()}`;
}

const FOLLOW_UP_FIELD_DEPENDENCIES = {
  current_condition: ["current_condition", "refined_problem_statement"],
  project_objective: ["project_objective", "refined_problem_statement"],
  business_impact: ["business_impact", "refined_problem_statement"],
  scope: ["scope", "refined_problem_statement"],
  scope_guardrails: ["scope"],
  ctqs: ["operational_definition"],
  stakeholder_requirements: [],
  operational_definition: ["operational_definition"],
};

function deriveAffectedProblemFieldsFromAnswers(answers) {
  const seen = new Set();
  const affected = [];
  answers.forEach((item) => {
    const field = String(item?.field || "").trim();
    const dependencies = FOLLOW_UP_FIELD_DEPENDENCIES[field] || [];
    dependencies.forEach((dependency) => {
      if (seen.has(dependency)) return;
      seen.add(dependency);
      affected.push(dependency);
    });
  });
  return affected;
}

function toStringArray(items) {
  if (!Array.isArray(items)) return [];
  return items
    .map((item) => String(item || "").trim())
    .filter(Boolean);
}

function normalizeScopeGuardrails(scopeGuardrails) {
  const payload = scopeGuardrails && typeof scopeGuardrails === "object" ? scopeGuardrails : {};
  return {
    in_scope: toStringArray(payload.in_scope),
    out_of_scope: toStringArray(payload.out_of_scope),
    assumptions: toStringArray(payload.assumptions),
    constraints: toStringArray(payload.constraints),
    risks: toStringArray(payload.risks),
  };
}

function buildCtqDraftFromProblemRefine(data) {
  const ctqs = toStringArray(data?.ctqs).map((item) => ({
    ctq_metric: item,
    voc: "Inferred from the current Problem framing",
    requirement: item,
    target: "Validate baseline and target during Define and Measure",
    priority: "medium",
  }));
  const stakeholderNeeds = toStringArray(data?.stakeholder_requirements).map((item) => ({
    stakeholder: "Key stakeholder",
    need: item,
    success_metric: "Validate the measurable requirement during Define",
  }));
  if (!ctqs.length && !stakeholderNeeds.length) return null;
  return {
    ctqs,
    stakeholder_needs: stakeholderNeeds,
  };
}

export default function DefinePhase({ projectId, token }) {
  const [activeTab, setActiveTab] = useState("problem");
  const [loading, setLoading] = useState(true);
  const [defineLoadError, setDefineLoadError] = useState("");
  const [defineReloadToken, setDefineReloadToken] = useState(0);
  const [saving, setSaving] = useState(false);
  const [refining, setRefining] = useState(false);
  const [problemAiAssisted, setProblemAiAssisted] = useState(false);
  const [smartGoalAiDraftActive, setSmartGoalAiDraftActive] = useState(false);
  const [sipocAiDraftActive, setSipocAiDraftActive] = useState(false);
  const [stakeholdersAiDraftActive, setStakeholdersAiDraftActive] = useState(false);
  const [sipocSuggestionError, setSipocSuggestionError] = useState("");
  const [pendingSipocSuggestion, setPendingSipocSuggestion] = useState(null);
  const [sipocPreviewOpen, setSipocPreviewOpen] = useState(false);
  const [sipocSource, setSipocSource] = useState("user");
  const [sipocVersions, setSipocVersions] = useState([]);
  const [selectedSipocSections, setSelectedSipocSections] = useState(createSipocSectionSelectionState());
  const [sipocSectionModes, setSipocSectionModes] = useState(createSipocSectionModeState());

  // AI enhancement states
  const [aiGenerating, setAiGenerating] = useState("");
  const [aiCharter, setAiCharter] = useState(null);
  const [aiCtqs, setAiCtqs] = useState(null);
  const [aiScope, setAiScope] = useState(null);
  const [aiProblemMissingInfo, setAiProblemMissingInfo] = useState([]);
  const [aiProblemFollowUps, setAiProblemFollowUps] = useState([]);
  const [aiProblemFollowUpAnswers, setAiProblemFollowUpAnswers] = useState({});
  const [aiProblemAssumptions, setAiProblemAssumptions] = useState([]);
  const [aiProblemUpdating, setAiProblemUpdating] = useState(false);
  const [aiFinancialSuggestion, setAiFinancialSuggestion] = useState(null);
  const [confirmOperationalDefinitionOverwriteOpen, setConfirmOperationalDefinitionOverwriteOpen] = useState(false);
  const [aiAssistStatus, setAiAssistStatus] = useState({
    ctqs: { error: "", lastUpdated: "" },
    scope: { error: "", lastUpdated: "" },
    charter: { error: "", lastUpdated: "" },
    operational_definition: { error: "", lastUpdated: "" },
  });

  // Form states
  const [problemStatement, setProblemStatement] = useState({
    problem_text: "", current_state: "", desired_state: "", impact: "", scope: "",
  });
  const [problemOperationalDefinition, setProblemOperationalDefinitionState] = useState(normalizeOperationalDefinitionState({}));
  const [problemDirty, setProblemDirty] = useState(false);
  const [smartGoal, setSmartGoal] = useState({
    specific: "", measurable: "", achievable: "", relevant: "", time_bound: "",
  });
  const [sipoc, setSipoc] = useState({
    suppliers: [""], inputs: [""], process_steps: [""], outputs: [""], customers: [""],
  });
  const [stakeholders, setStakeholders] = useState([
    createBlankStakeholder(),
  ]);
  const [financialImpact, setFinancialImpact] = useState(normalizeFinancialImpactForm());
  const [calculatedFinancials, setCalculatedFinancials] = useState(null);
  const [financialErrors, setFinancialErrors] = useState({});
  const [problemErrors, setProblemErrors] = useState({});
  const [problemSavedNotice, setProblemSavedNotice] = useState("");
  const [smartSavedNotice, setSmartSavedNotice] = useState("");
  const [sipocSavedNotice, setSipocSavedNotice] = useState("");
  const problemStatementRef = useRef(problemStatement);
  const problemOperationalDefinitionRef = useRef(problemOperationalDefinition);
  const aiProblemDraftSnapshotRef = useRef(null);
  const aiProblemOperationalDefinitionSnapshotRef = useRef(null);
  const problemEditVersionRef = useRef(0);
  const sipocLastSavedRef = useRef(JSON.stringify(sipoc));
  const sipocAutosaveTimeoutRef = useRef(null);

  useEffect(() => {
    problemStatementRef.current = problemStatement;
  }, [problemStatement]);

  useEffect(() => {
    problemOperationalDefinitionRef.current = problemOperationalDefinition;
  }, [problemOperationalDefinition]);

  useEffect(() => {
    if (!problemSavedNotice) return undefined;
    const timeoutId = setTimeout(() => setProblemSavedNotice(""), 2000);
    return () => clearTimeout(timeoutId);
  }, [problemSavedNotice]);

  useEffect(() => {
    if (!smartSavedNotice) return undefined;
    const timeoutId = setTimeout(() => setSmartSavedNotice(""), 2000);
    return () => clearTimeout(timeoutId);
  }, [smartSavedNotice]);

  useEffect(() => {
    if (!sipocSavedNotice) return undefined;
    const timeoutId = setTimeout(() => setSipocSavedNotice(""), 2000);
    return () => clearTimeout(timeoutId);
  }, [sipocSavedNotice]);

  const applyLoadedProblemStatement = (nextProblemStatement, options = {}) => {
    const { preserveMissingOperationalDefinition = true } = options;
    const normalizedProblemStatement = normalizeProblemStatementState(nextProblemStatement);
    setProblemStatement(normalizedProblemStatement);
    problemStatementRef.current = normalizedProblemStatement;
    const hasOperationalDefinitionPayload = Boolean(
      nextProblemStatement?.operational_definition || nextProblemStatement?.operationalDefinition,
    );
    const normalizedOperationalDefinition = hasOperationalDefinitionPayload
      ? normalizeOperationalDefinitionState(
        nextProblemStatement?.operational_definition || nextProblemStatement?.operationalDefinition || {},
      )
      : preserveMissingOperationalDefinition
        ? problemOperationalDefinitionRef.current
        : normalizeOperationalDefinitionState({});
    setProblemOperationalDefinitionState(normalizedOperationalDefinition);
    problemOperationalDefinitionRef.current = normalizedOperationalDefinition;
    setProblemDirty(false);
  };

  const updateProblemStatement = (updater) => {
    setProblemStatement((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      problemStatementRef.current = next;
      setProblemAiAssisted(false);
      setProblemSavedNotice("");
      if (next.impact?.trim()) {
        setProblemErrors((currentErrors) => {
          if (!currentErrors.impact) return currentErrors;
          const { impact, ...rest } = currentErrors;
          return rest;
        });
      }
      return next;
    });
    problemEditVersionRef.current += 1;
    setProblemDirty(true);
  };

  const updateProblemOperationalDefinition = (updater) => {
    setProblemOperationalDefinitionState((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      problemOperationalDefinitionRef.current = next;
      setProblemAiAssisted(false);
      setProblemSavedNotice("");
      updateAiAssistStatus("operational_definition", { error: "", lastUpdated: "" });
      return next;
    });
    problemEditVersionRef.current += 1;
    setProblemDirty(true);
  };

  const updateSmartGoal = (updater) => {
    setSmartGoal((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      return next;
    });
    setSmartGoalAiDraftActive(false);
    setSmartSavedNotice("");
  };

  const updateAiAssistStatus = (toolKey, nextValues) => {
    setAiAssistStatus((current) => ({
      ...current,
      [toolKey]: {
        ...current[toolKey],
        ...nextValues,
      },
    }));
  };

  const updateAiProblemFollowUpAnswer = (questionKey, value) => {
    setAiProblemFollowUpAnswers((current) => ({
      ...current,
      [questionKey]: value,
    }));
  };

  const applyAiProblemDraftResponse = (data, options = {}) => {
    const {
      preserveManualEdits = false,
      affectedDraftFields = null,
    } = options;
    const currentProblem = problemStatementRef.current;
    const normalizedDraft = normalizeProblemStatementState({
      problem_text: data?.refined_problem_statement || data?.refined,
      current_condition: data?.current_condition,
      project_objective: data?.project_objective,
      business_impact: data?.business_impact,
      scope: data?.scope,
    });
    const previousAiSnapshot = aiProblemDraftSnapshotRef.current;
    const previousOperationalDefinitionSnapshot = aiProblemOperationalDefinitionSnapshotRef.current;
    const formFieldMap = {
      refined_problem_statement: "problem_text",
      current_condition: "current_state",
      project_objective: "desired_state",
      business_impact: "impact",
      scope: "scope",
    };
    const allowedDraftFields = Array.isArray(affectedDraftFields) && affectedDraftFields.length
      ? new Set(affectedDraftFields)
      : null;
    const nextProblem = { ...currentProblem };
    const nextAiSnapshot = previousAiSnapshot
      ? { ...previousAiSnapshot }
      : normalizeProblemStatementState({});
    const normalizedOperationalDefinition = normalizeOperationalDefinitionState(data?.operational_definition);
    const hasOperationalDefinitionDraft = Boolean(
      normalizedOperationalDefinition.metric_name
        || normalizedOperationalDefinition.issue_definition
        || normalizedOperationalDefinition.counting_rule
        || normalizedOperationalDefinition.measurement_basis
        || normalizedOperationalDefinition.validation_notes.length,
    );
    const allowOperationalDefinitionDraft = !allowedDraftFields || allowedDraftFields.has("operational_definition");
    const nextOperationalDefinition = { ...problemOperationalDefinitionRef.current };
    const nextOperationalDefinitionSnapshot = previousOperationalDefinitionSnapshot
      ? { ...previousOperationalDefinitionSnapshot }
      : normalizeOperationalDefinitionState({});

    Object.entries(formFieldMap).forEach(([draftField, formField]) => {
      if (allowedDraftFields && !allowedDraftFields.has(draftField)) {
        return;
      }
      const candidateValue = normalizedDraft[formField];
      if (!candidateValue) {
        return;
      }
      if (preserveManualEdits && previousAiSnapshot) {
        const currentValue = String(currentProblem[formField] || "").trim();
        const previousAiValue = String(previousAiSnapshot[formField] || "").trim();
        if (currentValue && currentValue !== previousAiValue) {
          return;
        }
      }
      nextProblem[formField] = candidateValue;
      nextAiSnapshot[formField] = candidateValue;
    });

    setProblemStatement(nextProblem);
    problemStatementRef.current = nextProblem;
    aiProblemDraftSnapshotRef.current = nextAiSnapshot;

    if (hasOperationalDefinitionDraft && allowOperationalDefinitionDraft) {
      Object.keys(normalizedOperationalDefinition).forEach((fieldKey) => {
        const candidateValue = fieldKey === "validation_notes"
          ? normalizedOperationalDefinition.validation_notes
          : normalizedOperationalDefinition[fieldKey];
        const hasValue = fieldKey === "validation_notes"
          ? candidateValue.length > 0
          : Boolean(candidateValue);

        if (!hasValue) {
          return;
        }

        if (preserveManualEdits && previousOperationalDefinitionSnapshot) {
          const currentValue = fieldKey === "validation_notes"
            ? JSON.stringify(problemOperationalDefinitionRef.current.validation_notes || [])
            : String(problemOperationalDefinitionRef.current[fieldKey] || "").trim();
          const previousAiValue = fieldKey === "validation_notes"
            ? JSON.stringify(previousOperationalDefinitionSnapshot.validation_notes || [])
            : String(previousOperationalDefinitionSnapshot[fieldKey] || "").trim();
          if (currentValue && currentValue !== previousAiValue) {
            return;
          }
        }

        nextOperationalDefinition[fieldKey] = candidateValue;
        nextOperationalDefinitionSnapshot[fieldKey] = candidateValue;
      });

      setProblemOperationalDefinitionState(nextOperationalDefinition);
      problemOperationalDefinitionRef.current = nextOperationalDefinition;
      aiProblemOperationalDefinitionSnapshotRef.current = nextOperationalDefinitionSnapshot;
    }

    const aiCtqDraft = buildCtqDraftFromProblemRefine(data);
    if (aiCtqDraft) {
      setAiCtqs(aiCtqDraft);
      updateAiAssistStatus("ctqs", {
        error: "",
        lastUpdated: new Date().toISOString(),
      });
    }

    const aiScopeDraft = normalizeScopeGuardrails(data?.scope_guardrails);
    if (
      aiScopeDraft.in_scope.length
      || aiScopeDraft.out_of_scope.length
      || aiScopeDraft.assumptions.length
      || aiScopeDraft.constraints.length
      || aiScopeDraft.risks.length
      || aiScopeDraft.notes.length
    ) {
      setAiScope(aiScopeDraft);
      updateAiAssistStatus("scope", {
        error: "",
        lastUpdated: new Date().toISOString(),
      });
    }

    setAiProblemMissingInfo(Array.isArray(data?.missing_information) ? data.missing_information : []);
    setAiProblemFollowUps(Array.isArray(data?.follow_up_questions) ? data.follow_up_questions : []);
    setAiProblemAssumptions(Array.isArray(data?.assumptions_or_unvalidated_items) ? data.assumptions_or_unvalidated_items : []);
    setProblemAiAssisted(true);
    setProblemDirty(true);
    setProblemSavedNotice("");
    return nextProblem;
  };

  // ---- Data fetch ----
  useEffect(() => {
    let cancelled = false;

    const loadDefinePhase = async () => {
      setLoading(true);
      setDefineLoadError("");
      setProblemAiAssisted(false);
      setRefining(false);
      setAiGenerating("");
      setAiCtqs(null);
      setAiScope(null);
      setAiCharter(null);
      setAiProblemMissingInfo([]);
      setAiProblemFollowUps([]);
      setAiProblemFollowUpAnswers({});
      setAiProblemAssumptions([]);
      setAiProblemUpdating(false);
      aiProblemDraftSnapshotRef.current = null;
      aiProblemOperationalDefinitionSnapshotRef.current = null;
      setAiAssistStatus({
        ctqs: { error: "", lastUpdated: "" },
        scope: { error: "", lastUpdated: "" },
        charter: { error: "", lastUpdated: "" },
        operational_definition: { error: "", lastUpdated: "" },
      });
      const blankProblemStatement = normalizeProblemStatementState({});
      const blankOperationalDefinition = normalizeOperationalDefinitionState({});
      setProblemStatement(blankProblemStatement);
      problemStatementRef.current = blankProblemStatement;
      setProblemOperationalDefinitionState(blankOperationalDefinition);
      problemOperationalDefinitionRef.current = blankOperationalDefinition;
      setProblemDirty(false);
      setProblemSavedNotice("");

      try {
        const defineResponse = await fetch(`${API}/projects/${projectId}/define`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!defineResponse.ok) {
          let message = "Failed to load the Define phase.";
          try {
            const errorData = await defineResponse.json();
            message = errorData?.detail || message;
          } catch {
            // Keep the fallback message when the response body is unavailable.
          }
          throw new Error(message);
        }

        const data = await defineResponse.json();
        if (cancelled) return;

        if (data.problem_statement) {
          applyLoadedProblemStatement(data.problem_statement, {
            preserveMissingOperationalDefinition: false,
          });
        }
        if (data.operational_definition) {
          const normalizedOperationalDefinition = normalizeOperationalDefinitionState(data.operational_definition);
          setProblemOperationalDefinitionState(normalizedOperationalDefinition);
          problemOperationalDefinitionRef.current = normalizedOperationalDefinition;
        }
        if (data.smart_goal) setSmartGoal(data.smart_goal);
        if (data.sipoc) {
          const loadedSipoc = {
            suppliers: normalizeDefineList(data.sipoc?.suppliers),
            inputs: normalizeDefineList(data.sipoc?.inputs),
            process_steps: normalizeDefineList(data.sipoc?.process_steps),
            outputs: normalizeDefineList(data.sipoc?.outputs),
            customers: normalizeDefineList(data.sipoc?.customers),
          };
          setSipoc(loadedSipoc);
          sipocLastSavedRef.current = JSON.stringify(loadedSipoc);
          setSipocSource("user");
        }
        if (data.sipoc_source) setSipocSource(data.sipoc_source === "ai_generated" ? "ai" : "user");
        setSipocVersions(Array.isArray(data.sipoc_versions) ? data.sipoc_versions : []);
        if (data.stakeholder_map?.stakeholders?.length) {
          setStakeholders(normalizeStakeholderDraft(data.stakeholder_map.stakeholders));
        }
        if (data.financial_impact) {
          setFinancialImpact(normalizeFinancialImpactForm(data.financial_impact));
          setCalculatedFinancials(normalizeFinancialImpactResponse(data));
        }
        if (data.ai_charter) setAiCharter(data.ai_charter);
      } catch (error) {
        if (cancelled) return;
        const message = error instanceof Error ? error.message : "Failed to load the Define phase.";
        console.error("define_phase_load_failed", { projectId, message, error });
        setDefineLoadError(message);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadDefinePhase();

    return () => {
      cancelled = true;
    };
  }, [projectId, token, defineReloadToken]);

  // ---- Save helpers ----
  const saveEndpoint = async (path, body, successMsg) => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/projects/${projectId}/define/${path}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        let message = "Failed to save";
        try {
          const errorData = await response.json();
          message = errorData?.detail || message;
        } catch {
          // Fall back to generic error message when response body is unavailable.
        }
        throw new Error(message);
      }
      const data = await response.json();
      toast.success(successMsg);
      return data;
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const saveProblemStatement = async () => {
    const payload = {
      ...problemStatementRef.current,
      operational_definition: problemOperationalDefinitionRef.current,
    };
    const validationErrors = validateProblemStatement(payload);
    setProblemErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      toast.error("Add Business Impact before saving the Define problem statement.");
      return;
    }
    const desiredStateWarning = getDesiredStateWarning(payload);
    const data = await saveEndpoint("problem-statement", payload, "Problem statement saved");
    if (data?.problem_statement) {
      applyLoadedProblemStatement(data.problem_statement);
      setProblemErrors({});
      setProblemSavedNotice("Problem Statement Saved");
      if (desiredStateWarning) {
        toast.info("Desired State was saved with a provisional target. Confirm the baseline in Measure before treating that target as validated.");
      }
    }
  };
  const saveSmartGoal = async () => {
    const data = await saveEndpoint("smart-goal", smartGoal, "SMART goal saved");
    if (data?.smart_goal) {
      setSmartSavedNotice("SMART Goal Complete");
    }
  };
  const saveSipoc = async (options = {}) => {
    const { silent = false } = options;
    const buildSipocBody = (sipocData, sourceValue) => ({
      ...sipocData,
      source: sourceValue === "ai" ? "ai_generated" : "user_modified",
    });
    const body = buildSipocBody(sipoc, sipocSource);
    if (silent) {
      setSaving(true);
      try {
        const response = await fetch(`${API}/projects/${projectId}/define/sipoc`, {
          method: "PUT",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify(body),
        });
        if (!response.ok) {
          let message = "Failed to autosave SIPOC";
          try {
            const errorData = await response.json();
            message = errorData?.detail || message;
          } catch {
            // ignore parse failure
          }
          throw new Error(message);
        }
        const data = await response.json();
        if (data?.sipoc) {
          sipocLastSavedRef.current = JSON.stringify(data.sipoc);
          setSipocVersions(data.sipoc_versions || []);
          if (data.sipoc_source) setSipocSource(data.sipoc_source === "ai_generated" ? "ai" : "user");
          setSipocSavedNotice("SIPOC Saved");
        }
        return data;
      } catch (error) {
        console.error("sipoc_autosave_failed", { projectId, message: error.message, body });
      } finally {
        setSaving(false);
      }
      return null;
    }

    const data = await saveEndpoint("sipoc", body, "SIPOC saved");
    if (data?.sipoc) {
      sipocLastSavedRef.current = JSON.stringify(data.sipoc);
      setSipocVersions(data.sipoc_versions || []);
      if (data.sipoc_source) setSipocSource(data.sipoc_source === "ai_generated" ? "ai" : "user");
      setSipocSavedNotice("SIPOC Saved");
    }
    return data;
  };
  const saveStakeholders = () => saveEndpoint("stakeholders", { stakeholders }, "Stakeholders saved");
  const saveFinancialImpact = async () => {
    const errors = validateFinancialImpactForm(financialImpact);
    setFinancialErrors(errors);
    if (Object.keys(errors).length > 0) {
      toast.error("Fix the financial inputs before saving.");
      return;
    }

    const data = await saveEndpoint("financial-impact", prepareFinancialImpactForSave(financialImpact), "Financial impact saved");
    if (data) {
      setCalculatedFinancials(normalizeFinancialImpactResponse(data));
      setFinancialErrors({});
    }
  };

  // ---- AI helpers ----
  const refineProblemWithAI = async () => {
    const baselineProblemStatement = problemStatementRef.current;
    if (!baselineProblemStatement.problem_text) { toast.error("Please enter a problem statement first"); return; }
    const requestEditVersion = problemEditVersionRef.current;
    setRefining(true);
    try {
      const response = await fetch(`${API}/ai/refine-problem`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          problem_text: baselineProblemStatement.problem_text,
          context: {
            current_state: baselineProblemStatement.current_state,
            desired_state: baselineProblemStatement.desired_state,
            impact: baselineProblemStatement.impact,
            scope: baselineProblemStatement.scope,
          },
        }),
      });
      if (await handleAiResponse(response)) {
        return;
      }
      if (!response.ok) {
        let message = "AI refinement failed";
        try {
          const errorData = await response.json();
          message = errorData?.detail?.message || errorData?.detail || message;
        } catch {
          // Fall back to generic message.
        }
        throw new Error(message);
      }

      const data = unwrapAi(await response.json());
      if (data?.ai_unavailable) {
        toast.error("AI refinement is unavailable right now. Please try again later.");
        return;
      }

      const refinedProblemText = String(data?.refined_problem_statement || data?.refined || "").trim();
      if (refinedProblemText) {
        if (problemEditVersionRef.current !== requestEditVersion) {
          toast.info("Manual edits changed while AI was running. AI text was not applied.");
          return;
        }

        const updatedPs = applyAiProblemDraftResponse(data);
        setAiProblemFollowUpAnswers({});
        toast.success("Problem page draft generated.");
        const savedData = await saveEndpoint("problem-statement", {
          ...updatedPs,
          operational_definition: problemOperationalDefinitionRef.current,
        }, "Refined problem statement saved");
        if (savedData?.problem_statement) {
          applyLoadedProblemStatement(savedData.problem_statement);
          setProblemSavedNotice("Problem Statement Saved");
          toast.info("AI-generated Problem draft auto-saved");
        } else {
          toast.error("AI draft was applied locally but could not be saved. Review and save again.");
        }
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "AI refinement failed");
    } finally {
      setRefining(false);
    }
  };

  const generateOperationalDefinitionWithAI = async () => {
    const baselineProblemStatement = problemStatementRef.current;
    const hadExistingOperationalDefinition = hasOperationalDefinitionContent(problemOperationalDefinitionRef.current);
    if (!baselineProblemStatement.problem_text) {
      toast.error("Add a problem description before generating an operational definition.");
      return;
    }

    setAiGenerating("Operational Definition");
    updateAiAssistStatus("operational_definition", { error: "" });
    try {
      const response = await fetch(`${API}/ai/refine-problem`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          problem_text: baselineProblemStatement.problem_text,
          context: {
            current_state: baselineProblemStatement.current_state,
            desired_state: baselineProblemStatement.desired_state,
            impact: baselineProblemStatement.impact,
            scope: baselineProblemStatement.scope,
          },
        }),
      });
      if (await handleAiResponse(response)) {
        updateAiAssistStatus("operational_definition", {
          error: "The AI request could not be completed with the current account or request state.",
        });
        return;
      }
      if (!response.ok) {
        let message = "Operational definition generation failed";
        try {
          const errorData = await response.json();
          message = errorData?.detail?.message || errorData?.detail || message;
        } catch {
          // Fall back to generic message.
        }
        throw new Error(message);
      }

      const data = unwrapAi(await response.json());
      if (data?.ai_unavailable) {
        updateAiAssistStatus("operational_definition", {
          error: "AI assistance is temporarily unavailable. Retry when the service is available.",
        });
        toast.error("Operational definition generation is unavailable right now.");
        return;
      }

      applyAiProblemDraftResponse(data, {
        affectedDraftFields: ["operational_definition"],
      });
      setProblemAiAssisted(true);
      updateAiAssistStatus("operational_definition", {
        error: "",
        lastUpdated: new Date().toISOString(),
      });
      toast.success(
        hadExistingOperationalDefinition
          ? "Operational definition draft refreshed."
          : "Operational definition draft generated.",
      );
      setProblemSavedNotice("");
      toast.info("Review the updated draft and click Save Problem to persist it.");
    } catch (error) {
      const message = error instanceof Error ? error.message : "Operational definition generation failed";
      updateAiAssistStatus("operational_definition", { error: message });
      toast.error(message);
    } finally {
      setAiGenerating("");
    }
  };

  const requestOperationalDefinitionGeneration = () => {
    if (hasOperationalDefinitionContent(problemOperationalDefinitionRef.current)) {
      setConfirmOperationalDefinitionOverwriteOpen(true);
      return;
    }
    generateOperationalDefinitionWithAI();
  };

  const confirmOperationalDefinitionOverwrite = () => {
    setConfirmOperationalDefinitionOverwriteOpen(false);
    generateOperationalDefinitionWithAI();
  };

  const applyProblemFollowUpAnswers = async () => {
    const answerItems = aiProblemFollowUps
      .map((item) => ({
        ...item,
        answer: String(aiProblemFollowUpAnswers[getFollowUpQuestionKey(item)] || "").trim(),
      }))
      .filter((item) => item.answer);

    if (!answerItems.length) {
      toast.error("Add at least one answer before updating the draft.");
      return;
    }

    const requestEditVersion = problemEditVersionRef.current;
    setAiProblemUpdating(true);
    try {
      const response = await fetch(`${API}/ai/refine-problem-follow-up`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          problem_text: problemStatementRef.current.problem_text,
          context: {
            current_state: problemStatementRef.current.current_state,
            desired_state: problemStatementRef.current.desired_state,
            impact: problemStatementRef.current.impact,
            scope: problemStatementRef.current.scope,
          },
          current_draft: {
            refined_problem_statement: problemStatementRef.current.problem_text,
            current_condition: problemStatementRef.current.current_state,
            project_objective: problemStatementRef.current.desired_state,
            business_impact: problemStatementRef.current.impact,
            scope: problemStatementRef.current.scope,
            operational_definition: problemOperationalDefinitionRef.current,
            ctqs: aiCtqs?.ctqs?.map((item) => item.ctq_metric).filter(Boolean) || [],
            stakeholder_requirements: aiCtqs?.stakeholder_needs?.map((item) => item.need).filter(Boolean) || [],
            scope_guardrails: aiScope || {},
            missing_information: aiProblemMissingInfo,
            follow_up_questions: aiProblemFollowUps,
            assumptions_or_unvalidated_items: aiProblemAssumptions,
          },
          follow_up_answers: answerItems,
        }),
      });

      if (await handleAiResponse(response)) {
        return;
      }
      if (!response.ok) {
        let message = "Problem update failed";
        try {
          const errorData = await response.json();
          message = errorData?.detail?.message || errorData?.detail || message;
        } catch {
          // Keep generic fallback.
        }
        throw new Error(message);
      }

      const data = unwrapAi(await response.json());
      if (data?.ai_unavailable) {
        toast.error("AI follow-up revision is unavailable right now. Please try again later.");
        return;
      }

      if (problemEditVersionRef.current !== requestEditVersion) {
        toast.info("Manual edits changed while AI was updating. Only unaffected fields were preserved.");
      }

      const affectedDraftFields = deriveAffectedProblemFieldsFromAnswers(answerItems);
      const updatedPs = applyAiProblemDraftResponse(data, {
        preserveManualEdits: true,
        affectedDraftFields,
      });
      setAiProblemFollowUpAnswers({});
      toast.success("Affected Problem sections updated.");
      const savedData = await saveEndpoint("problem-statement", {
        ...updatedPs,
        operational_definition: problemOperationalDefinitionRef.current,
      }, "Problem statement saved");
      if (savedData?.problem_statement) {
        applyLoadedProblemStatement(savedData.problem_statement);
        setProblemSavedNotice("Problem Statement Saved");
        toast.info("Updated Problem sections auto-saved");
      } else {
        toast.error("Updated sections were applied locally but could not be saved. Review and save again.");
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Problem update failed");
    } finally {
      setAiProblemUpdating(false);
    }
  };

  const aiCall = async (endpoint, body, setResult, label, toolKey) => {
    setAiGenerating(label);
    updateAiAssistStatus(toolKey, { error: "" });
    try {
      const response = await fetch(`${API}/ai/${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ project_id: projectId, ...body }),
      });
      if (await handleAiResponse(response)) {
        updateAiAssistStatus(toolKey, { error: "The AI request could not be completed with the current account or request state." });
        return;
      }
      if (!response.ok) {
        let message = "AI generation failed";
        try {
          const err = await response.json();
          message = err?.detail?.message || err?.detail?.reason || err?.detail || message;
        } catch {
          // Keep generic fallback message.
        }
        throw new Error(typeof message === "string" ? message : "AI generation failed");
      }

      const data = unwrapAi(await response.json());
      setResult(data);

      if (data?.ai_unavailable) {
        updateAiAssistStatus(toolKey, { error: "AI assistance is temporarily unavailable. The tool returned a fallback response instead of a draft." });
        toast.error("AI service unavailable. Please try again later.");
        return;
      }

      updateAiAssistStatus(toolKey, {
        error: "",
        lastUpdated: new Date().toISOString(),
      });
      toast.success(`${label} generated!`);
    } catch (error) {
      const message = error instanceof Error ? error.message : "AI generation failed";
      console.error("define_ai_assistance_failed", { endpoint, projectId, message, body, error });
      updateAiAssistStatus(toolKey, { error: message });
      toast.error(message);
    } finally {
      setAiGenerating("");
    }
  };

  const generateSmartGoal = () => aiCall("generate-smart-goal", { problem_text: problemStatement.problem_text }, (data) => {
    setSmartGoal(data);
    setSmartGoalAiDraftActive(true);
    toast.info("Review and adjust the AI-generated goal, then save.");
  }, "SMART Goal", "smart_goal");
  const generateCtqs = () => aiCall("suggest-ctqs", {
    problem_text: problemStatement.problem_text,
    impact: problemStatement.impact,
    scope: problemStatement.scope,
    stakeholders,
  }, setAiCtqs, "CTQ Suggestions", "ctqs");
  const generateScope = () => aiCall("suggest-scope", {
    problem_text: problemStatement.problem_text,
    current_state: problemStatement.current_state,
    impact: problemStatement.impact,
    scope: problemStatement.scope,
  }, setAiScope, "Scope Guardrails", "scope");
  const generateSipocSuggestion = async () => {
    const SIPOC_ERROR_MESSAGE = "SIPOC generation failed. Please check project data or try again.";
    const requestUrl = getProjectSipocSuggestRoute(API, projectId);
    const legacyRequestUrl = `${API}/ai/suggest-sipoc`;
    const requestBody = {
      problem_statement: problemStatement.problem_text,
      business_impact: problemStatement.impact,
      scope: problemStatement.scope,
      smart_goal: [
        smartGoal.specific,
        smartGoal.measurable,
        smartGoal.relevant,
      ].filter((value) => String(value || "").trim()).join(" | "),
    };
    const legacyRequestBody = {
      project_id: projectId,
      problem_text: problemStatement.problem_text,
      impact: problemStatement.impact,
      scope: problemStatement.scope,
      smart_goal: smartGoal,
    };

    setAiGenerating("SIPOC Draft");
    setSipocSuggestionError("");
    try {
      console.log("SIPOC URL:", requestUrl);
      console.log("SIPOC payload:", requestBody);

      let response = await fetch(requestUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(requestBody),
      });

      let activeRequestUrl = requestUrl;
      let activeRequestBody = requestBody;

      if (response.status === 404) {
        console.warn("suggest_sipoc_route_fallback", {
          projectId,
          missingUrl: requestUrl,
          fallbackUrl: legacyRequestUrl,
        });
        console.log("SIPOC URL:", legacyRequestUrl);
        console.log("SIPOC payload:", legacyRequestBody);
        response = await fetch(legacyRequestUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify(legacyRequestBody),
        });
        activeRequestUrl = legacyRequestUrl;
        activeRequestBody = legacyRequestBody;
      }

      if (!response.ok) {
        let errorDetail = "Unknown SIPOC suggestion failure";
        let errorBody = null;
        try {
          errorBody = await response.json();
          errorDetail = errorBody?.detail?.reason || errorBody?.detail?.message || errorBody?.detail || errorDetail;
        } catch {
          // Keep generic failure detail for logging.
        }
        console.error("suggest_sipoc_failed", {
          projectId,
          requestUrl: activeRequestUrl,
          status: response.status,
          detail: errorDetail,
          responseBody: errorBody,
          requestBody: activeRequestBody,
        });
        setSipocSuggestionError(SIPOC_ERROR_MESSAGE);
        toast.error(SIPOC_ERROR_MESSAGE);
        return;
      }

      const data = await response.json();
      if (data.ai_unavailable) {
        console.error("suggest_sipoc_failed", {
          projectId,
          requestUrl: activeRequestUrl,
          status: 503,
          detail: "AI unavailable",
          responseBody: data,
          requestBody: activeRequestBody,
        });
        setSipocSuggestionError(SIPOC_ERROR_MESSAGE);
        toast.error(SIPOC_ERROR_MESSAGE);
        return;
      }

      const nextSipoc = {
        suppliers: normalizeSipocDraftItems(data.suppliers?.length ? data.suppliers : [""]),
        inputs: normalizeSipocDraftItems(data.inputs?.length ? data.inputs : [""]),
        process_steps: normalizeSipocDraftItems(
          (data.process_steps?.length ? data.process_steps : data.process?.length ? data.process : [""]).slice(0, 7)
        ),
        outputs: normalizeSipocDraftItems(data.outputs?.length ? data.outputs : [""]),
        customers: normalizeSipocDraftItems(data.customers?.length ? data.customers : [""]),
      };
      setPendingSipocSuggestion(nextSipoc);
      setSelectedSipocSections(createSipocSectionSelectionState(true));
      setSipocSectionModes(createSipocSectionModeState(sipoc));
      setSipocPreviewOpen(true);
      setSipocSuggestionError("");
      toast.success("SIPOC Draft generated!");
      toast.info("Preview the SIPOC draft, edit it as needed, then apply all or apply individual sections.");
    } catch (error) {
      console.error("suggest_sipoc_failed", {
        projectId,
        requestUrl,
        message: error.message,
        error,
        requestBody,
        legacyRequestUrl,
      });
      setSipocSuggestionError(SIPOC_ERROR_MESSAGE);
      toast.error(SIPOC_ERROR_MESSAGE);
    } finally {
      setAiGenerating("");
    }
  };

  const setSipocReviewSectionSelected = (section, value) => {
    setSelectedSipocSections((prev) => ({ ...prev, [section]: value }));
  };

  const setSipocReviewSectionMode = (section, mode) => {
    setSipocSectionModes((prev) => ({ ...prev, [section]: mode }));
  };

  const confirmApplySipocSuggestion = () => {
    if (!pendingSipocSuggestion) return;
    const nextSipoc = { ...sipoc };
    let appliedCount = 0;

    SIPOC_SECTION_KEYS.forEach((section) => {
      const nextSection = resolveSipocSectionApplication(
        nextSipoc[section],
        pendingSipocSuggestion[section],
        sipocSectionModes[section] || "replace"
      );
      if (JSON.stringify(nextSipoc[section] || []) !== JSON.stringify(nextSection)) {
        nextSipoc[section] = nextSection;
        appliedCount += 1;
      }
    });

    if (appliedCount > 0) {
      setSipoc(nextSipoc);
      setSipocAiDraftActive(true);
      setSipocSource("ai");
    }
    setPendingSipocSuggestion(null);
    setSipocPreviewOpen(false);
    setSipocSuggestionError("");
    if (appliedCount > 0) {
      toast.success("SIPOC draft applied.");
      toast.info("Review the applied draft and save when ready.");
      return;
    }
    toast.info("No SIPOC sections were changed.");
  };

  const applySelectedSipocSuggestionSections = () => {
    if (!pendingSipocSuggestion) return;
    const selectedKeys = SIPOC_SECTION_KEYS.filter((section) => selectedSipocSections[section] && sipocSectionModes[section] !== "keep");
    if (selectedKeys.length === 0) {
      toast.info("Select at least one SIPOC section to apply.");
      return;
    }

    const nextSipoc = { ...sipoc };
    let appliedCount = 0;
    selectedKeys.forEach((section) => {
      const nextSection = resolveSipocSectionApplication(
        nextSipoc[section],
        pendingSipocSuggestion[section],
        sipocSectionModes[section] || "replace"
      );
      if (JSON.stringify(nextSipoc[section] || []) !== JSON.stringify(nextSection)) {
        nextSipoc[section] = nextSection;
        appliedCount += 1;
      }
    });

    if (appliedCount > 0) {
      setSipoc(nextSipoc);
      setSipocAiDraftActive(true);
      setSipocSource("ai");
    }

    setPendingSipocSuggestion(null);
    setSipocPreviewOpen(false);
    setSipocSuggestionError("");
    if (appliedCount > 0) {
      toast.success("Selected SIPOC sections applied.");
      toast.info("Review the applied sections and save when ready.");
      return;
    }
    toast.info("Selected SIPOC sections matched the current content, so nothing changed.");
  };

  const applySipocSuggestionSection = (section) => {
    if (!pendingSipocSuggestion) return;
    const nextSection = resolveSipocSectionApplication(
      sipoc[section],
      pendingSipocSuggestion[section],
      sipocSectionModes[section] || "replace"
    );
    const didApply = JSON.stringify(sipoc[section] || []) !== JSON.stringify(nextSection);
    if (didApply) {
      setSipoc((prev) => ({ ...prev, [section]: nextSection }));
      setSipocAiDraftActive(true);
      setSipocSource("ai");
      toast.info(`${section.replace("_", " ")} applied from the AI draft. Review and save when ready.`);
      setSelectedSipocSections((prev) => ({ ...prev, [section]: false }));
      return;
    }
    toast.info(`${section.replace("_", " ")} was not changed.`);
  };

  const updatePendingSipocItem = (category, index, value) => {
    setPendingSipocSuggestion((prev) => ({
      ...prev,
      [category]: prev[category].map((item, itemIndex) => (itemIndex === index ? value : item)),
    }));
  };

  const addPendingSipocItem = (category) => {
    setPendingSipocSuggestion((prev) => ({
      ...prev,
      [category]: [...prev[category], ""],
    }));
  };

  const removePendingSipocItem = (category, index) => {
    setPendingSipocSuggestion((prev) => ({
      ...prev,
      [category]: prev[category].length <= 1
        ? prev[category]
        : prev[category].filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  const cancelSipocPreview = () => {
    setPendingSipocSuggestion(null);
    setSipocPreviewOpen(false);
    setSelectedSipocSections(createSipocSectionSelectionState());
    setSipocSectionModes(createSipocSectionModeState(sipoc));
  };

  const restorePreviousSipocVersion = async () => {
    const previousVersion = sipocVersions[sipocVersions.length - 1];
    if (!previousVersion?.data) return;
    setSipoc(previousVersion.data);
    setSipocSource(previousVersion.source === "ai_generated" ? "ai" : "user");
    setPendingSipocSuggestion(null);
    setSipocPreviewOpen(false);
    setSaving(true);
    try {
      const response = await fetch(`${API}/projects/${projectId}/define/sipoc`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          ...previousVersion.data,
          source: previousVersion.source || "user_modified",
        }),
      });
      if (!response.ok) {
        throw new Error("Failed to restore SIPOC version");
      }
      const data = await response.json();
      sipocLastSavedRef.current = JSON.stringify(data.sipoc || previousVersion.data);
      setSipocVersions(data.sipoc_versions || []);
      if (data.sipoc_source) setSipocSource(data.sipoc_source === "ai_generated" ? "ai" : "user");
    } catch (error) {
      console.error("sipoc_restore_failed", { projectId, message: error.message, previousVersion });
    } finally {
      setSaving(false);
    }
    toast.info("Previous SIPOC version restored.");
  };
  const generateStakeholderSuggestion = () => aiCall("suggest-stakeholders", {}, (data) => {
    setStakeholders(normalizeStakeholderDraft(data.stakeholders));
    setStakeholdersAiDraftActive(true);
    toast.info("Review the stakeholder draft, edit as needed, then save.");
  }, "Stakeholder Draft", "stakeholders");
  const generateCharter = () => aiCall("generate-charter", {
    problem_text: problemStatement.problem_text,
    current_state: problemStatement.current_state,
    desired_state: problemStatement.desired_state,
    impact: problemStatement.impact,
    scope: problemStatement.scope,
    smart_goal: smartGoal,
    stakeholders,
    financial_impact: prepareFinancialImpactForSave(financialImpact),
  }, setAiCharter, "Project Charter", "charter");
  const generateFinancialSuggestion = () => aiCall("suggest-financial-impact", {}, setAiFinancialSuggestion, "Financial Estimate", "financial");

  // ---- SIPOC helpers ----
  const addSipocItem = (category) => {
    setSipocAiDraftActive(false);
    setSipocSource("user");
    setSipocSavedNotice("");
    setSipoc((prev) => ({ ...prev, [category]: [...prev[category], ""] }));
  };
  const updateSipocItem = (category, index, value) => {
    setSipocAiDraftActive(false);
    setSipocSource("user");
    setSipocSavedNotice("");
    setSipoc((prev) => ({ ...prev, [category]: prev[category].map((item, i) => (i === index ? value : item)) }));
  };
  const removeSipocItem = (category, index) => {
    if (sipoc[category].length <= 1) return;
    setSipocAiDraftActive(false);
    setSipocSource("user");
    setSipocSavedNotice("");
    setSipoc((prev) => ({ ...prev, [category]: prev[category].filter((_, i) => i !== index) }));
  };

  useEffect(() => {
    const currentSipocJson = JSON.stringify(sipoc);
    if (currentSipocJson === sipocLastSavedRef.current) {
      return undefined;
    }

    if (sipocAutosaveTimeoutRef.current) {
      clearTimeout(sipocAutosaveTimeoutRef.current);
    }

    sipocAutosaveTimeoutRef.current = setTimeout(() => {
      saveSipoc({ silent: true });
    }, 1000);

    return () => {
      if (sipocAutosaveTimeoutRef.current) {
        clearTimeout(sipocAutosaveTimeoutRef.current);
      }
    };
  }, [sipoc, sipocSource]);

  // ---- Stakeholder helpers ----
  const addStakeholder = () => {
    setStakeholdersAiDraftActive(false);
    setStakeholders((prev) => [...prev, createBlankStakeholder()]);
  };
  const updateStakeholder = (index, field, value) => {
    setStakeholdersAiDraftActive(false);
    setStakeholders((prev) => prev.map((s, i) => (i === index ? { ...s, [field]: value } : s)));
  };
  const removeStakeholder = (index) => {
    if (stakeholders.length <= 1) return;
    setStakeholdersAiDraftActive(false);
    setStakeholders((prev) => prev.filter((_, i) => i !== index));
  };
  const applyStakeholderTemplate = () => {
    setStakeholders(normalizeStakeholderDraft(STAKEHOLDER_TEMPLATE));
    toast.info("Lean Six Sigma stakeholder template loaded. Review and edit before saving.");
  };

  // ---- Export ----
  const exportCharter = async () => {
    try {
      const response = await fetch(`${API}/projects/${projectId}/define/export-charter`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error("Export failed");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "project_charter.pdf";
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Charter exported");
    } catch (error) {
      toast.error(error.message);
    }
  };

  if (loading) return <div className="text-muted-foreground">Loading Define phase...</div>;

  const desiredStateWarning = getDesiredStateWarning(problemStatement);
  const smartMeasurableWarning = getSmartMeasurableWarning(smartGoal);
  const sipocValidation = validateSipoc(sipoc);
  const sipocQuality = scoreSipocQuality(sipoc, {
    problemStatement: problemStatement.problem_text,
    businessImpact: problemStatement.impact,
    scope: problemStatement.scope,
  });
  const sipocHasUnsavedChanges = JSON.stringify(sipoc) !== sipocLastSavedRef.current;
  const sipocWorkspaceState = pendingSipocSuggestion
    ? "Draft (AI)"
    : sipocHasUnsavedChanges
      ? (sipocSource === "ai" ? "Draft (AI)" : "Modified")
      : "Saved";
  const defineGateArtifacts = {
    problem_statement: Boolean(problemStatement.problem_text?.trim()),
    business_impact: Boolean(problemStatement.impact?.trim()),
    smart_goal: Boolean(smartGoal.specific?.trim() && smartGoal.measurable?.trim() && smartGoal.time_bound?.trim()),
    sipoc: sipocValidation.isValid && sipocQuality.score >= SIPOC_QUALITY_VALID_THRESHOLD,
    stakeholders: Boolean(stakeholders.some((stakeholder) => (stakeholder.role || stakeholder.name || "").trim())),
    financials: Boolean(calculatedFinancials),
  };
  const defineGateRequired = Object.keys(DEFINE_GATE_LABELS);
  const readyForGateReview = defineGateRequired.every((key) => defineGateArtifacts[key]);
  const missingDefineSections = defineGateRequired.filter((key) => !defineGateArtifacts[key]);
  const completedDefineCount = defineGateRequired.filter((key) => defineGateArtifacts[key]).length;
  const nextDefineStepLabel = missingDefineSections.length > 0
    ? DEFINE_GATE_LABELS[missingDefineSections[0]] ?? missingDefineSections[0]
    : "Gate Review";

  return (
    <div className="space-y-4" data-testid="define-phase">
      {defineLoadError && (
        <div className="flex flex-col gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-100" data-testid="define-load-error">
          <div className="flex items-start gap-2">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-300" />
            <div className="space-y-1">
              <p className="font-medium text-amber-200">Define page data did not finish loading.</p>
              <p className="text-xs leading-5 text-amber-100/90">
                The page is still available with a safe fallback state. Retry loading the project data, then continue editing once the connection recovers.
              </p>
            </div>
          </div>
          <div className="flex justify-start">
            <Button
              type="button"
              variant="outline"
              className="border-amber-400/30 bg-transparent text-amber-100 hover:bg-amber-500/10 hover:text-amber-50"
              onClick={() => setDefineReloadToken((current) => current + 1)}
            >
              Retry Define Load
            </Button>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-1">
          <h1 className="font-heading text-2xl font-bold">Define Phase</h1>
          <p className="text-sm text-muted-foreground">Establish project scope, goals, and stakeholder alignment</p>
        </div>
        <Button onClick={exportCharter} variant="outline" className="gap-2" data-testid="export-charter-btn">
          <Download className="w-4 h-4" />
          Export Charter
        </Button>
      </div>

      <div className="rounded-xl border border-border bg-card px-4 py-3 shadow-sm" data-testid="define-completeness-panel">
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Define Gate Completeness</p>
              <span className="rounded-full border border-border/70 bg-background/40 px-2.5 py-1 text-[11px] font-semibold text-foreground" data-testid="define-progress">
                {completedDefineCount}/{defineGateRequired.length} complete
              </span>
              <div
                className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider ${
                  readyForGateReview
                    ? "border-emerald-500/25 bg-emerald-500/10 text-emerald-400"
                    : "border-amber-500/25 bg-amber-500/10 text-amber-400"
                }`}
                data-testid="define-ready-status"
              >
                {readyForGateReview ? "Ready for Review" : "In Progress"}
              </div>
            </div>

            <div className="text-xs text-muted-foreground" data-testid="define-missing-summary">
              <span className="font-medium text-foreground">Next:</span>{" "}
              <span>{readyForGateReview ? "Prepare Define gate review" : nextDefineStepLabel}</span>
              {!defineGateArtifacts.sipoc && (
                <span className="ml-2 text-amber-200" data-testid="define-missing-sipoc-detail">
                  SIPOC still needs detail.
                </span>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-1.5">
            {defineGateRequired.map((key) => {
              const complete = Boolean(defineGateArtifacts[key]);
              const detail = complete
                ? `${DEFINE_GATE_LABELS[key]} complete`
                : key === "sipoc"
                  ? `SIPOC incomplete or insufficient detail. ${sipocValidation.issues[0] || sipocQuality.feedback[0] || `Quality score is ${sipocQuality.score}/100.`}`
                  : `${DEFINE_GATE_LABELS[key]} missing or not yet valid.`;

              return (
                <div
                  key={key}
                  title={detail}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold tracking-wide ${
                    complete
                      ? "border-emerald-500/25 bg-emerald-500/10 text-emerald-300"
                      : "border-amber-500/25 bg-amber-500/10 text-amber-300"
                  }`}
                  data-testid={`define-completion-${key}`}
                >
                  {complete ? (
                    <CheckCircle2 className="h-3.5 w-3.5" />
                  ) : (
                    <AlertCircle className="h-3.5 w-3.5" />
                  )}
                  <span>{DEFINE_GATE_LABELS[key]}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="h-auto w-full justify-start gap-1 rounded-xl border border-border bg-card p-1">
          <TabsTrigger value="problem" className="gap-2 px-3 py-2 data-[state=active]:bg-background data-[state=active]:shadow-sm" data-testid="tab-problem">
            <FileText className="w-4 h-4" /> Problem
            {defineGateArtifacts.problem_statement && defineGateArtifacts.business_impact && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />}
          </TabsTrigger>
          <TabsTrigger value="goal" className="gap-2 px-3 py-2 data-[state=active]:bg-background data-[state=active]:shadow-sm" data-testid="tab-goal">
            <Target className="w-4 h-4" /> SMART Goal
            {defineGateArtifacts.smart_goal && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />}
          </TabsTrigger>
          <TabsTrigger value="sipoc" className="gap-2 px-3 py-2 data-[state=active]:bg-background data-[state=active]:shadow-sm" data-testid="tab-sipoc">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
            SIPOC
            {defineGateArtifacts.sipoc && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />}
          </TabsTrigger>
          <TabsTrigger value="stakeholders" className="gap-2 px-3 py-2 data-[state=active]:bg-background data-[state=active]:shadow-sm" data-testid="tab-stakeholders">
            <Users className="w-4 h-4" /> Stakeholders
            {defineGateArtifacts.stakeholders && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />}
          </TabsTrigger>
          <TabsTrigger value="financial" className="gap-2 px-3 py-2 data-[state=active]:bg-background data-[state=active]:shadow-sm" data-testid="tab-financial">
            <DollarSign className="w-4 h-4" /> Financial
            {defineGateArtifacts.financials && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="problem" className="space-y-6">
          <ProblemTab
            projectId={projectId} token={token}
            problemStatement={problemStatement} setProblemStatement={updateProblemStatement}
            operationalDefinition={problemOperationalDefinition}
            setOperationalDefinition={updateProblemOperationalDefinition}
            saving={saving} refining={refining} aiGenerating={aiGenerating} problemDirty={problemDirty}
            aiCtqs={aiCtqs} aiScope={aiScope} aiCharter={aiCharter}
            aiProblemMissingInfo={aiProblemMissingInfo}
            aiProblemFollowUps={aiProblemFollowUps}
            aiProblemFollowUpAnswers={aiProblemFollowUpAnswers}
            aiProblemAssumptions={aiProblemAssumptions}
            aiProblemUpdating={aiProblemUpdating}
            aiAssistStatus={aiAssistStatus}
            problemAiAssisted={problemAiAssisted}
            operationalDefinitionAiStatus={aiAssistStatus.operational_definition}
            confirmOperationalDefinitionOverwriteOpen={confirmOperationalDefinitionOverwriteOpen}
            desiredStateWarning={desiredStateWarning} problemErrors={problemErrors}
            savedNotice={problemSavedNotice}
            saveProblemStatement={saveProblemStatement}
            refineProblemWithAI={refineProblemWithAI}
            requestOperationalDefinitionGeneration={requestOperationalDefinitionGeneration}
            setConfirmOperationalDefinitionOverwriteOpen={setConfirmOperationalDefinitionOverwriteOpen}
            confirmOperationalDefinitionOverwrite={confirmOperationalDefinitionOverwrite}
            updateAiProblemFollowUpAnswer={updateAiProblemFollowUpAnswer}
            applyProblemFollowUpAnswers={applyProblemFollowUpAnswers}
            generateCtqs={generateCtqs} generateScope={generateScope} generateCharter={generateCharter}
            continueToSmartGoal={() => setActiveTab("goal")}
          />
        </TabsContent>

        <TabsContent value="goal" className="space-y-6">
          <SmartGoalTab
            projectId={projectId} token={token}
            smartGoal={smartGoal} setSmartGoal={updateSmartGoal}
            saving={saving} aiGenerating={aiGenerating}
            hasProblemText={!!problemStatement.problem_text}
            measurableWarning={smartMeasurableWarning}
            aiDraftActive={smartGoalAiDraftActive}
            savedNotice={smartSavedNotice}
            saveSmartGoal={saveSmartGoal} generateSmartGoal={generateSmartGoal}
            continueToSipoc={() => setActiveTab("sipoc")}
          />
        </TabsContent>

        <TabsContent value="sipoc" className="space-y-6">
          <SipocTab
            sipoc={sipoc} saving={saving} aiGenerating={aiGenerating}
            hasProblemText={!!problemStatement.problem_text}
            aiDraftActive={sipocAiDraftActive}
            sipocSource={sipocSource}
            sipocWorkspaceState={sipocWorkspaceState}
            savedNotice={sipocSavedNotice}
            suggestionError={sipocSuggestionError}
            pendingSuggestion={pendingSipocSuggestion}
            previewOpen={sipocPreviewOpen}
            setPreviewOpen={setSipocPreviewOpen}
            sipocVersions={sipocVersions}
            sipocQuality={sipocQuality}
            selectedSections={selectedSipocSections}
            sectionModes={sipocSectionModes}
            setSectionSelected={setSipocReviewSectionSelected}
            setSectionMode={setSipocReviewSectionMode}
            restorePreviousSipocVersion={restorePreviousSipocVersion}
            confirmApplySuggestion={confirmApplySipocSuggestion}
            applySelectedSuggestionSections={applySelectedSipocSuggestionSections}
            applySuggestionSection={applySipocSuggestionSection}
            updatePendingSipocItem={updatePendingSipocItem}
            addPendingSipocItem={addPendingSipocItem}
            removePendingSipocItem={removePendingSipocItem}
            cancelSipocPreview={cancelSipocPreview}
            regenerateSipocSuggestion={generateSipocSuggestion}
            addSipocItem={addSipocItem} updateSipocItem={updateSipocItem}
            removeSipocItem={removeSipocItem} saveSipoc={saveSipoc}
            generateSipocSuggestion={generateSipocSuggestion}
            continueToStakeholders={() => setActiveTab("stakeholders")}
          />
        </TabsContent>

        <TabsContent value="stakeholders" className="space-y-6">
          <StakeholdersTab
            stakeholders={stakeholders} saving={saving} aiGenerating={aiGenerating}
            hasProblemText={!!problemStatement.problem_text}
            aiDraftActive={stakeholdersAiDraftActive}
            addStakeholder={addStakeholder} updateStakeholder={updateStakeholder}
            removeStakeholder={removeStakeholder} saveStakeholders={saveStakeholders}
            applyStakeholderTemplate={applyStakeholderTemplate}
            generateStakeholderSuggestion={generateStakeholderSuggestion}
          />
        </TabsContent>

        <TabsContent value="financial" className="space-y-6">
          <FinancialTab
            projectId={projectId} token={token}
            financialImpact={financialImpact} setFinancialImpact={setFinancialImpact}
            calculatedFinancials={calculatedFinancials}
            validationErrors={financialErrors}
            saving={saving} aiGenerating={aiGenerating}
            aiFinancialSuggestion={aiFinancialSuggestion}
            hasProblemText={!!problemStatement.problem_text}
            saveFinancialImpact={saveFinancialImpact}
            generateFinancialSuggestion={generateFinancialSuggestion}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
